/**
 * Credit Warning Notification System
 *
 * Shows toast notifications when users are running low on credits:
 * - Warning at 20% remaining
 * - Critical warning at 10% remaining
 *
 * Uses localStorage to track which warnings have been shown this billing period
 * to avoid repeat notifications.
 *
 * @module components/credit-warnings
 */

const STORAGE_KEY = 'dripscribe_credit_warnings';
const API_ENDPOINT = '/api/credit-status/';

/**
 * Get the current warning state from localStorage
 * @returns {Object} Warning state object
 */
function getWarningState() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[credit-warnings] Error reading localStorage:', e);
  }
  return {
    warning20Shown: false,
    warning10Shown: false,
    periodStart: null,
  };
}

/**
 * Save warning state to localStorage
 * @param {Object} state - Warning state to save
 */
function saveWarningState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('[credit-warnings] Error saving to localStorage:', e);
  }
}

/**
 * Reset warning state (call when new billing period starts)
 * @param {string} periodStart - ISO date string of period start
 */
function resetWarningState(periodStart) {
  saveWarningState({
    warning20Shown: false,
    warning10Shown: false,
    periodStart: periodStart,
  });
}

/**
 * Check credit status and show warnings if needed
 */
async function checkCreditWarnings() {
  // Only run on dashboard pages (avoid running on public pages)
  if (!document.body.classList.contains('authenticated') &&
      !window.location.pathname.startsWith('/dashboard')) {
    return;
  }

  try {
    const response = await fetch(API_ENDPOINT, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      // User might not be authenticated or endpoint not available
      if (response.status === 403 || response.status === 401) {
        return; // Silently ignore auth errors
      }
      console.warn('[credit-warnings] API returned:', response.status);
      return;
    }

    const data = await response.json();
    const state = getWarningState();

    // Check if we need to reset state for new billing period
    // (This is a simple check - if initial credits are different, probably new period)
    if (data.credits_initial > 0 && data.credits_available > data.credits_initial * 0.9) {
      // User has more than 90% credits - likely a new period or just started
      // Reset warnings so they can trigger again
      if (state.warning10Shown || state.warning20Shown) {
        resetWarningState(new Date().toISOString());
        return; // Don't show warnings right after reset
      }
    }

    const percentage = data.percentage_remaining;

    // Check for trial expiration warning (5 days or less)
    if (data.trial_days_remaining > 0 && data.trial_days_remaining <= 5) {
      showTrialWarning(data.trial_days_remaining);
    }

    // Check for critical (10%) warning first
    if (percentage <= 10 && !state.warning10Shown) {
      showCreditWarning(
        'warning',
        `You have only ${percentage}% of your credits remaining (${data.credits_available} credits). ` +
        'Consider upgrading your plan to continue sending emails.',
        'Low Credits Alert'
      );
      state.warning10Shown = true;
      state.warning20Shown = true; // Also mark 20% as shown
      saveWarningState(state);
    }
    // Check for 20% warning
    else if (percentage <= 20 && percentage > 10 && !state.warning20Shown) {
      showCreditWarning(
        'info',
        `You're down to ${percentage}% of your monthly credits (${data.credits_available} remaining).`,
        'Credit Usage Notice'
      );
      state.warning20Shown = true;
      saveWarningState(state);
    }

  } catch (error) {
    // Network errors, etc - fail silently
    console.warn('[credit-warnings] Failed to check credit status:', error);
  }
}

/**
 * Show a credit warning notification
 * @param {string} type - 'warning' or 'info'
 * @param {string} message - Message to display
 * @param {string} title - Title of the notification
 */
function showCreditWarning(type, message, title) {
  // Use the global showNotification if available (from swal-config.js)
  if (typeof window.showNotification === 'function') {
    window.showNotification(type, message, title);
  } else if (typeof Swal !== 'undefined') {
    // Fallback to direct Swal usage
    Swal.fire({
      icon: type,
      title: title,
      text: message,
      confirmButtonText: 'View Plans',
      showCancelButton: true,
      cancelButtonText: 'Dismiss',
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = '/pricing/';
      }
    });
  } else {
    // Last resort - browser alert
    console.warn(`[Credit Warning] ${title}: ${message}`);
  }
}

/**
 * Show trial expiration warning
 * @param {number} daysRemaining - Days left in trial
 */
function showTrialWarning(daysRemaining) {
  const storageKey = 'dripscribe_trial_warning_shown';
  const today = new Date().toDateString();

  // Only show once per day
  try {
    const lastShown = localStorage.getItem(storageKey);
    if (lastShown === today) {
      return;
    }
    localStorage.setItem(storageKey, today);
  } catch (e) {
    // Continue even if localStorage fails
  }

  const dayText = daysRemaining === 1 ? 'day' : 'days';
  const message = `Your free trial ends in ${daysRemaining} ${dayText}. Upgrade now to keep your access and continue sending personalized emails.`;

  if (typeof window.showNotification === 'function') {
    window.showNotification('warning', message, 'Trial Ending Soon');
  } else if (typeof Swal !== 'undefined') {
    Swal.fire({
      icon: 'warning',
      title: 'Trial Ending Soon',
      text: message,
      confirmButtonText: 'Upgrade Now',
      showCancelButton: true,
      cancelButtonText: 'Remind Me Later',
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = '/pricing/';
      }
    });
  }
}

/**
 * Initialize credit warnings on page load
 */
function initCreditWarnings() {
  // Delay check to not block page rendering
  setTimeout(checkCreditWarnings, 1500);
}

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', initCreditWarnings);

// Export for manual use
export { checkCreditWarnings, resetWarningState };
